import { useMutation } from "react-query";
import { useAppContext } from "../AppProviders";
import { API_URL } from "./helpers";

export interface LegacyReportSummaryItem {
  uuid: string;
  reportId: string;
  personId: string;
  flagKitVersionId: string;
  companyId: string;
  name: string;
  firstName: string;
  middleName: string;
  lastName: string;
  dateOfBirth: number;
  role: string | null;
  emailList: Array<{ email: string }>;
  aliasList: Array<{ name: string }>;
  phoneList: Array<{ number: string }>;
  locationList: Array<{
    formattedAddress: string;
    line1: string;
    line2: string;
    city: string;
    state: string;
    country: string;
    startDate: number | null;
    endDate: number | null;
  }>;
  degreeList: any[];
  jobList: any[];
  dateModified: number;
  datesFinished: number[];
  datesProcessing: number[];
  personStatus: string;
  reviewStatus: string;
  updateType: string;
  numberOfReports: number;
  webPersonMetrics: {
    badNewsFlagCount: number;
    badPostFlagCount: number;
    goodNewsFlagCount: number;
    goodPostFlagCount: number;
    keywordNewsFlagCount: number;
    keywordPostFlagCount: number;
    newsFlagCountsByKeyword: Record<string, number>;
    newsFlagCountsByType: Record<string, number>;
    personUuid: string;
    postFlagCountsByKeyword: Record<string, number>;
    postFlagCountsByProfileType: Record<string, number>;
    postFlagCountsByType: Record<string, number>;
    postFlagTypeCountsByPostType: Record<string, number>;
    profileUrlsByProfileType: Record<string, string[]>;
    relevantNewsFlagCount: number;
    totalBadFlagCount: number;
    totalGoodFlagCount: number;
    totalNewsFlagCount: number;
    totalPostFlagCount: number;
  };
  creator: string | null;
  screeningCompanyName: string;
  date: string;
  flagCount: number | null;
  flagTypes: any | null;
  mediaEntries: any[];
  searchId: string;
  searchName: string;
  searchUpToDate: boolean;
  candidateStatus: string | null;
  contactEmail: string | null;
  investigator: boolean;
  lookbackWindow: number;
  paused: boolean;
  percentAccepted: number | null;
  permission: string | null;
  permissions: string | null;
  processingMetrics: any | null;
  profiles: any | null;
  priority: string | null;
  visiblePriority: string;
}

export interface MediaEntry {
  mediaUrl: string | null;
  mediaType: "IMAGE" | "VIDEO" | "LINK";
  thumbnailUrl: string | null;
  famaUrl: string | null;
}

export interface Post {
  uuid: string;
  siteId: string;
  url: string;
  date: number;
  text: string;
  profileHandle: string | null;
  profileType: string;
  postType: "LIKED" | "SHARED" | "ORIGINAL";
  comments: any[];
  status: string | null;
  flaggedKeywords: string[];
  flaggedStemmableKeywords: string[];
  flaggedSensitivities: string[];
  priority: string | null;
  permission: string | null;
  addedInLastRerun: boolean;
  source: string;
  mediaEntries: MediaEntry[];
}

export interface Article {
  uuid: string;
  personUuid: string;
  text: string;
  contentDate: number | null;
  contentFamaDate: number | null;
  webDataType: string | null;
  title: string;
  originalUrl: string;
  indexUrl: string;
  displayUrl: string;
  source: string | null;
  webDataProviderType: string | null;
  relevant: boolean;
  personSearchContentDecision: string | null;
  flaggedKeywords: string[];
  flaggedStemmableKeywords: string[];
  flaggedSensitivities: string[];
  addedInLastRerun: boolean;
  excluded: boolean;
  priority: string | null;
  permission: string | null;
  probability: number;
  articleQueries: string | null;
  addedManually: boolean;
}

export function useGetLegacyReport(reportUUID: string) {
  const { requestApi } = useAppContext();

  const getLegacyReport = async (
    reportUUID: string
  ): Promise<LegacyReportSummaryItem> => {
    return requestApi
      .get(`${API_URL}/legacy/reports/${reportUUID}`)
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (reportUUID: string) => getLegacyReport(reportUUID),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useGetLegacySocialPosts(reportUUID: string) {
  const { requestApi } = useAppContext();

  const getLegacyPosts = async (
    reportUUID: string,
    page: number
  ): Promise<Post[]> => {
    const params: string = `?pageNumber=${page - 1}`;
    return requestApi
      .get(`${API_URL}/legacy/reports/${reportUUID}/posts${params}`)
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    ({ reportUUID, page }: { reportUUID: string; page: number }) =>
      getLegacyPosts(reportUUID, page),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useGetLegacyArticles(reportUUID: string) {
  const { requestApi } = useAppContext();

  const getLegacyArticles = async ({
    reportUUID,
    page,
  }: {
    reportUUID: string;
    page: number;
  }): Promise<Article[]> => {
    const params: string = `?pageNumber=${page - 1}`;
    return requestApi
      .get(`${API_URL}/legacy/reports/${reportUUID}/articles${params}`)
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    ({ reportUUID, page }: { reportUUID: string; page: number }) =>
      getLegacyArticles({ reportUUID, page }),
    {}
  );

  return {
    ...mutationResult,
  };
}
